import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, message, Alert } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchTickets,
  type TicketSummary,
} from '../../services/supportTicketsService';
import { FiltersBar, type SupportFiltersValues } from './components/FiltersBar';
import { TicketsTable } from './components/TicketsTable';
import { EmptyState } from '../../design-system/EmptyState';

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const PAGE_SIZE = 20;

export default function SupportTicketsListPage() {
  const { user } = useAuth();
  const currentAdminId = user?.id ?? null;

  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<SupportFiltersValues>({
    q: '',
    status: undefined,
    priority: undefined,
    category: undefined,
    assignee: undefined,
    source: undefined,
  });

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof fetchTickets>[0] = {
        page,
        limit: pageSize,
        q: filters.q?.trim() || undefined,
        status: filters.status,
        priority: filters.priority,
        category: filters.category,
        assignee: filters.assignee,
        source: filters.source,
      };
      const res = await fetchTickets(params);
      if (res.success && res.data) {
        setTickets(res.data.tickets);
        setTotal(res.data.pagination?.total ?? 0);
      } else {
        setTickets([]);
        setTotal(0);
        setError(res.message || 'Không tải được danh sách');
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(ax.response?.data?.message || ax.message || 'Lỗi tải dữ liệu');
      message.error('Không tải được danh sách ticket');
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleSearch = () => loadTickets();
  const handleReset = () => {
    setFilters({
      q: '',
      status: undefined,
      priority: undefined,
      category: undefined,
      assignee: undefined,
      source: undefined,
    });
    setPage(1);
  };

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Support Tickets</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            Hỗ trợ kỹ thuật
          </span>
        </nav>
      </header>

      <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 24 }}>
        <FiltersBar
          values={filters}
          onChange={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          loading={loading}
          currentAdminId={currentAdminId}
        />

        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!error && tickets.length === 0 && !loading && (
          <EmptyState
            description="Chưa có ticket nào"
            onRefresh={loadTickets}
            onClearFilters={handleReset}
          />
        )}

        {!error && (tickets.length > 0 || loading) && (
          <TicketsTable
            data={tickets}
            loading={loading}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={(p, size) => {
              setPage(p);
              if (size) setPageSize(size);
            }}
          />
        )}
      </Card>
    </div>
  );
}
